'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { cloneElement, isValidElement, PureComponent } from 'react';
import styled, { css } from 'styled-components';
import H4 from '../elements/headings/H4';
import UISplitLayout from '../layout/UISplitLayout';
import { EMPTY_STATE_PADDING_X, EMPTY_STATE_PADDING_Y } from 'HubStyleTokens/sizes';
import { toPx } from '../utils/Styles';
import { warnIfFragment } from '../utils/devWarnings'; // empty state styling is characterized by copious whitespace, per design
// vertical orientation differs to more closely match UIErrorMessage styles

const OUTER_MARGIN = 40;
const Outer = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, ["flush", "layout", "maxWidth"]);

  return /*#__PURE__*/_jsx(UISplitLayout, Object.assign({}, rest));
}).withConfig({
  displayName: "UIEmptyState__Outer",
  componentId: "vyyiq7-0"
})(["margin:", " auto;margin-bottom:", ";margin-top:", ";max-width:", ";padding-left:", ";width:100%;"], props => props.layout === 'vertical' ? toPx(OUTER_MARGIN) : toPx(OUTER_MARGIN * 2), props => props.flush && '0', props => props.flush && '0', props => toPx(props.maxWidth), EMPTY_STATE_PADDING_X);
const Inner = styled(UISplitLayout.defaultProps.InnerWrapper).withConfig({
  displayName: "UIEmptyState__Inner",
  componentId: "vyyiq7-1"
})(["margin-left:-", ";margin-top:-", ";"], EMPTY_STATE_PADDING_X, EMPTY_STATE_PADDING_Y);
const SLOT_PADDING_STYLES = css(["margin:auto;padding-left:", ";padding-top:", ";"], EMPTY_STATE_PADDING_X, EMPTY_STATE_PADDING_Y);
const PrimarySlot = styled(UISplitLayout.defaultProps.Primary).withConfig({
  displayName: "UIEmptyState__PrimarySlot",
  componentId: "vyyiq7-2"
})(["", ";margin-top:0;text-align:", ";"], SLOT_PADDING_STYLES, props => props.layout === 'vertical' && 'center');
const SecondarySlot = styled(UISplitLayout.defaultProps.Secondary).withConfig({
  displayName: "UIEmptyState__SecondarySlot",
  componentId: "vyyiq7-3"
})(["", ";margin-bottom:", ";margin-top:0;text-align:", ";"], SLOT_PADDING_STYLES, props => props.layout === 'vertical' && '20px', props => props.layout === 'vertical' && 'center');
export default class UIEmptyState extends PureComponent {
  constructor(...args) {
    super(...args);

    this.Primary = props => {
      const {
        layout,
        _primaryContentWidth,
        reverseOrder,
        secondaryContent
      } = this.props;
      const computedPrimaryContentWidth = secondaryContent ? _primaryContentWidth : '100%';
      return /*#__PURE__*/_jsx(PrimarySlot, Object.assign({}, props, {
        basis: layout === 'horizontal' ? computedPrimaryContentWidth : 'auto',
        grow: 9999,
        layout: layout,
        order: reverseOrder ? 1 : 0,
        shrink: 1
      }));
    };

    this.Secondary = props => {
      const {
        layout,
        secondaryContentWidth
      } = this.props;
      const computedPadding = parseInt(EMPTY_STATE_PADDING_X, 10);
      const computedSecondaryContentWidth = layout === 'horizontal' ? secondaryContentWidth + computedPadding : 'auto';
      return /*#__PURE__*/_jsx(SecondarySlot, Object.assign({}, props, {
        basis: computedSecondaryContentWidth,
        grow: 1,
        layout: layout,
        shrink: 1
      }));
    };
  }

  render() {
    const _this$props = this.props,
          {
      className,
      layout,
      primaryContent,
      primaryContentClassName,
      secondaryContent,
      secondaryContentClassName,
      titleText
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["className", "layout", "primaryContent", "primaryContentClassName", "_primaryContentWidth", "reverseOrder", "secondaryContent", "secondaryContentClassName", "secondaryContentWidth", "titleText"]);

    let renderedHeading;

    if ( /*#__PURE__*/isValidElement(titleText) && !titleText.type.isI18nElement) {
      warnIfFragment(titleText, UIEmptyState.displayName, 'titleText');
      renderedHeading = titleText;
    } else if (titleText != null) {
      renderedHeading = /*#__PURE__*/_jsx(H4, {
        children: titleText
      });
    }

    warnIfFragment(primaryContent, UIEmptyState.displayName, 'primaryContent');
    const renderedPrimaryContent = [renderedHeading != null ? /*#__PURE__*/cloneElement(renderedHeading, {
      key: 'heading '
    }) : renderedHeading, /*#__PURE__*/isValidElement(primaryContent) ? /*#__PURE__*/cloneElement(primaryContent, {
      key: 'content'
    }) : primaryContent];
    return /*#__PURE__*/_jsx(Outer, Object.assign({}, rest, {
      className: className,
      layout: layout,
      orientation: layout,
      InnerWrapper: Inner,
      Primary: this.Primary,
      primaryClassName: primaryContentClassName,
      primaryContent: renderedPrimaryContent,
      Secondary: this.Secondary,
      secondaryClassName: secondaryContentClassName,
      secondaryContent: secondaryContent
    }));
  }

}
UIEmptyState.propTypes = {
  /**
   * Removes the default vertical margins for the component.
   */
  flush: PropTypes.bool.isRequired,

  /**
   * Sets the layout direction for the content.
   */
  layout: PropTypes.oneOf(['horizontal', 'vertical']),

  /**
   * The max-width for the main container.
   */
  maxWidth: PropTypes.number,

  /**
   * The main informational content for the empty state.
   */
  primaryContent: PropTypes.node.isRequired,

  /**
   * A class applied to the container of `primaryContent`.
   */
  primaryContentClassName: PropTypes.string,

  /**
   * _primaryContentWidth:
   * This prop is kept as an escape hatch, but 200 is used as the minimum
   * threshold for keeping text readable as horizontal space squishes down
   */
  _primaryContentWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Swaps the visual order of the primary and secondary content.
   * This ensures the primary content is still presented first to assistive technology.
   */
  reverseOrder: PropTypes.bool.isRequired,

  /**
   * The accompanying visual for the empty state. This is most often an image or illustration.
   */
  secondaryContent: PropTypes.node,

  /**
   * A class applied to the container of `secondaryContent`.
   */
  secondaryContentClassName: PropTypes.string,

  /**
   * Sets the initial size of the secondary content.
   * Uses `flex-basis` under the hood so this may shrink or grow depending on space available.
   */
  secondaryContentWidth: PropTypes.number,

  /**
   * The text for the title header rendered in `primaryContent`.
   * In most cases this will just be a text node, but supports heading component nodes as well.
   */
  titleText: PropTypes.node
};
UIEmptyState.defaultProps = {
  flush: false,
  layout: 'horizontal',
  maxWidth: 815,
  reverseOrder: false,
  secondaryContentWidth: 350,
  _primaryContentWidth: 200
};
UIEmptyState.displayName = 'UIEmptyState';